import React from 'react';
import { cn } from '@/lib/utils';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  hint?: string;
  onCheckedChange?: (checked: boolean) => void;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, error, hint, id, onCheckedChange, onChange, ...props }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = Boolean(error);
    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      onCheckedChange?.(!!e.target.checked);
      onChange?.(e);
    };
    
    return (
      <div className="flex flex-col">
        <div className="flex items-start">
          <input
            ref={ref}
            id={checkboxId}
            type="checkbox"
            className={cn(
              'h-4 w-4 mt-0.5 rounded border border-input bg-background text-primary-600 focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              hasError && 'border-error-500',
              className
            )}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${checkboxId}-error` : hint ? `${checkboxId}-hint` : undefined}
            onChange={handleChange}
            {...props}
          />
          {label && (
            <label
              htmlFor={checkboxId}
              className="ml-2 text-sm font-medium text-foreground cursor-pointer select-none"
            >
              {label}
            </label>
          )}
        </div>
        
        {hint && !error && (
          <p id={`${checkboxId}-hint`} className="mt-1 ml-6 text-sm text-muted-foreground">
            {hint}
          </p>
        )}
        
        {error && (
          <p id={`${checkboxId}-error`} className="mt-1 ml-6 text-sm text-error-600">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
